<template>
  <div
    class="group tw-flex flex-col cursor-pointer rounded-[10px] border border-black/[0.07] overflow-hidden bg-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg will-change-transform"
    @click="$emit('click')"
  >
    <!-- Image / placeholder -->
    <div class="relative h-[120px] overflow-hidden bg-gray-100 shrink-0">
      <img
        v-if="getImageForCommunity(community)"
        :src="getImageForCommunity(community)"
        :alt="community.name"
        class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        @error="$event.target.style.display='none'"
      />
      <div
        v-else
        class="w-full h-full tw-flex items-center justify-center"
        :style="{ background: variant === 'suggested' ? '#e5e7eb' : (community.color || 'var(--primary, #2563eb)') }"
      >
        <span
          class="text-[2rem] font-bold uppercase"
          :class="variant === 'suggested' ? 'text-gray-400' : 'text-white/70'"
        >{{ community.name?.charAt(0) }}</span>
      </div>

      <!-- Gradient overlay -->
      <div
        class="absolute inset-0"
        :class="variant === 'suggested' ? 'bg-gradient-to-t from-black/30 to-transparent' : 'bg-gradient-to-t from-black/55 to-transparent'"
      />

      <!-- 3-dot menu (joined only) -->
      <div v-if="variant === 'joined'" class="absolute top-2 right-2 z-10" @click.stop>
        <button
          class="inline-flex items-center justify-center w-[26px] h-[26px] rounded-md border-0 bg-black/30 backdrop-blur-sm text-white cursor-pointer hover:bg-black/50 transition-colors p-0"
          @click.stop="menuOpen = !menuOpen"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="2"/>
            <circle cx="12" cy="12" r="2"/>
            <circle cx="12" cy="19" r="2"/>
          </svg>
        </button>
        <div
          v-if="menuOpen"
          class="absolute top-full right-0 mt-1 z-50 min-w-[130px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        >
          <button
            class="block w-full px-3 py-2 text-xs font-medium text-left border-0 cursor-pointer text-red-600 hover:bg-red-50 transition-colors"
            @click.stop="openLeaveCommunityModal"
          >
            {{ $t('communities_leave') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Body -->
    <div class="tw-flex flex-col gap-1.5 p-3 flex-1">
      <h5 class="text-[0.82rem] font-semibold text-gray-900 m-0 leading-snug line-clamp-2">{{ community.name }}</h5>
      <p class="text-[0.72rem] text-gray-500 leading-relaxed m-0 line-clamp-2">{{ community.description }}</p>

      <!-- Joined: go-to CTA -->
      <span v-if="variant === 'joined'" class="inline-flex items-center gap-1 mt-auto pt-1.5 text-[0.72rem] font-semibold text-primary">
        {{ $t('personal_home_go_to_community') }}
        <Icon name="arrow-right" size="12"/>
      </span>

      <!-- Suggested: request access button -->
      <button
        v-else
        :disabled="hasRequestedAccess"
        :class="[
          'mt-2 px-2.5 py-1 text-[0.72rem] font-semibold border-0 rounded-md self-start transition-opacity',
          hasRequestedAccess
            ? 'bg-gray-100 text-gray-400 cursor-default'
            : 'bg-primary text-white cursor-pointer hover:opacity-80',
        ]"
        @click.stop="requestAccess"
      >
        {{ hasRequestedAccess ? $t('communities_access_requested') : $t('personal_home_request_access') }}
      </button>
    </div>
  </div>
</template>

<script>
  import { getImageForCommunity } from '@/util/helpers'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { ACTION_TYPES } from '@/store/modules/user'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import { requestAccessToCommunity } from '@/api/communities'

  export default {
    name: 'PersonalHomeCommunityCard',
    props: {
      community: {
        type: Object,
        required: true,
      },
      variant: {
        type: String,
        default: 'joined',
        validator: (v) => ['joined', 'suggested'].includes(v),
      },
    },
    emits: ['click'],
    data () {
      return {
        menuOpen: false,
      }
    },
    computed: {
      hasRequestedAccess () {
        return this.$store.getters.requestedToJoinCommunityIds.includes(this.community.id)
      },
    },
    methods: {
      getImageForCommunity,
      openLeaveCommunityModal () {
        this.menuOpen = false
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          title: this.$t('communities_leave_confirmation_title', { community: this.community.name }),
          body: this.$t('communities_leave_confirmation_message'),
          confirmLabel: this.$t('personal_home_relevant_yes'),
          modalBodyClasses: 'empty-fields-list',
          type: 'check-empty-fields',
          cancelLabel: this.$t('personal_home_relevant_no'),
          isCancellable: true,
          communityId: this.community.id,
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.CONFIRMATION)
      },
      requestAccess () {
        requestAccessToCommunity(this.community.id)
          .then(() => {
            this.$store.dispatch(ACTION_TYPES.REFRESH_PROFILE)
          })
          .catch((error) => {
            console.log(error)
          })
      },
    },
  }
</script>
